import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useClickOutside } from '@mantine/hooks';
import { Box, Flex, Text, TextInput } from '@mantine/core';
import ReportCellComment from '../../../../components/ReportComments/ReportCellComment';
import { NewTableCellWrapper } from './style';
import commonStore from '../../../../../../store/common';
import { useDebouncedValue } from '@mantine/hooks';
import { TbClipboardText } from 'react-icons/tb';
import { HEADER_BG } from '../tableConstants';
import { idCreator } from '../../../../../Project/utils/idCreator';

export const TextCell = ({
  value,
  onChange,
  error = null,
  cellRefs = null,
  cellKey = null,
  onCellKeyDown = null,
  fundId = null,
  fundName = null,
  tableName = null,
  fieldName = null,
  fieldOrder = null,
  isFromStaticTemplate = false
}) => {
  const { isViewer } = commonStore;

  const [isFocused, setIsFocused] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  // Comment icon/popover mounts lazily on first hover (see SelectCellView).
  const [commentMounted, setCommentMounted] = useState(false);
  const cellRef = useRef(null);
  const inputRef = useRef(null);
  const cellId = idCreator([fundId, tableName, fieldName, fieldOrder].filter(Boolean).join('-'));

  const [debouncedDraft] = useDebouncedValue(draft, 500);
  const lastCommittedRef = useRef(value);

  useEffect(() => {
    if (!isOpened) {
      setDraft(value ?? '');
      lastCommittedRef.current = value;
    }
  }, [value, isOpened]);

  useEffect(() => {
    if (!isOpened) return;
    if (debouncedDraft === lastCommittedRef.current) return;
    lastCommittedRef.current = debouncedDraft;
    onChange(debouncedDraft);
  }, [debouncedDraft, isOpened, onChange]);

  const onFocus = next => {
    setIsFocused(next);
  };

  const onOpen = next => {
    if (!next && isOpened) {
      if (draft !== lastCommittedRef.current) {
        lastCommittedRef.current = draft;
        onChange(draft);
      }
    }
    setIsOpened(next);
    onFocus(next);
  };

  const clickOutsideRef = useClickOutside(() => onOpen(false));

  // Shift the comment icon left of any trailing icons so it doesn't overlap them.
  const commentRightOffset = useMemo(() => {
    let offset = 12;
    if (isFromStaticTemplate) offset += 16;
    return offset;
  }, [isFromStaticTemplate]);

  const combinedRef = useCallback(
    node => {
      if (node) {
        clickOutsideRef.current = node;
        cellRef.current = node;
        if (cellRefs?.register && cellKey) {
          const refObj = { current: node };
          cellRefs.register(cellKey, refObj);
        }
      } else {
        if (cellRefs?.register && cellKey) {
          cellRefs.register(cellKey, null);
        }
      }
    },
    [clickOutsideRef, cellRefs, cellKey]
  );

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !isOpened) {
      e.preventDefault();
      e.stopPropagation();
      onOpen(true);
      return;
    }

    if (!onCellKeyDown || !cellKey) return;

    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      if (isOpened && e.target.tagName === 'INPUT') {
        const input = e.target;
        const isAtStart = input.selectionStart === 0 && input.selectionEnd === 0;
        const isAtEnd =
          input.selectionStart === input.value.length && input.selectionEnd === input.value.length;

        if (
          (e.key === 'ArrowLeft' && isAtStart) ||
          (e.key === 'ArrowRight' && isAtEnd) ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown'
        ) {
          e.preventDefault();
          e.stopPropagation();
          onCellKeyDown(e, cellKey);
        }
      } else {
        e.preventDefault();
        e.stopPropagation();
        onCellKeyDown(e, cellKey);
      }
    }
  };

  return (
    <NewTableCellWrapper
      sx={() => ({
        border: isFocused || isOpened ? '1px solid #228BE6' : '1px solid transparent'
      })}
      ref={combinedRef}
      onClick={() => onFocus(true)}
      onDoubleClick={() => onOpen(true)}
      onKeyDown={handleKeyDown}
      onBlur={() => onFocus(false)}
      tabIndex={0}
      h="100%"
      outline="none"
      pos="relative"
      $commentRightOffset={commentRightOffset}
      onPointerEnter={() => setCommentMounted(true)}
    >
      {isOpened ? (
        <TextInput
          id={`formpf-text-cell-${cellId}`}
          ref={inputRef}
          w="100%"
          styles={{
            input: {
              border: 'none',
              height: '100%',
              fontSize: '12px',
              lineHeight: '17px',
              '&:disabled': {
                backgroundColor: HEADER_BG
              }
            }
          }}
          h="100%"
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isViewer || isFromStaticTemplate}
        />
      ) : (
        <Box p="7.5px 12px" h="100%">
          <Flex align="center" gap="4px" justify="space-between">
            <Text id={`formpf-text-cell-display-${cellId}`} fz="12px" lh="17px">
              {value}
            </Text>
            {isFromStaticTemplate && (
              <TbClipboardText style={{ minWidth: '12px' }} fontSize="12px" />
            )}
          </Flex>
          {error && (
            <Text id={`formpf-text-cell-error-${cellId}`} fz="12px" mt="4px" c="red" lh="17px">
              {error.msg}
            </Text>
          )}
        </Box>
      )}
      <ReportCellComment
        fundId={fundId}
        fundName={fundName}
        tableName={tableName}
        fieldName={fieldName}
        fieldOrder={fieldOrder}
        cellHovered={commentMounted}
      />
    </NewTableCellWrapper>
  );
};
